import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Account, AccountType, AccountSubType, CreateAccountDto, UpdateAccountDto } from '../../api/accounts';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';

interface AccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateAccountDto | UpdateAccountDto) => Promise<void>;
  account?: Account | null;
  accounts: Account[]; // For parent account selection
  loading?: boolean;
}

interface FormData {
  code: string;
  name: string;
  description: string;
  type: AccountType;
  subType: AccountSubType;
  parentAccountId: string;
  metadata: {
    bankName?: string;
    accountNumber?: string;
    branchCode?: string;
    currency?: string;
    [key: string]: any;
  };
}

const ACCOUNT_TYPE_OPTIONS = [
  { value: AccountType.ASSET, label: 'Asset' },
  { value: AccountType.LIABILITY, label: 'Liability' },
  { value: AccountType.EQUITY, label: 'Equity' },
  { value: AccountType.REVENUE, label: 'Revenue' },
  { value: AccountType.EXPENSE, label: 'Expense' },
];

const ACCOUNT_SUBTYPE_OPTIONS = {
  [AccountType.ASSET]: [
    { value: AccountSubType.CURRENT_ASSET, label: 'Current Asset' },
    { value: AccountSubType.FIXED_ASSET, label: 'Fixed Asset' },
    { value: AccountSubType.INVENTORY, label: 'Inventory' },
    { value: AccountSubType.BANK, label: 'Bank' },
    { value: AccountSubType.CASH, label: 'Cash' },
    { value: AccountSubType.RECEIVABLE, label: 'Receivable' },
  ],
  [AccountType.LIABILITY]: [
    { value: AccountSubType.CURRENT_LIABILITY, label: 'Current Liability' },
    { value: AccountSubType.LONG_TERM_LIABILITY, label: 'Long Term Liability' },
    { value: AccountSubType.PAYABLE, label: 'Payable' },
  ],
  [AccountType.EQUITY]: [
    { value: AccountSubType.CAPITAL, label: 'Capital' },
    { value: AccountSubType.RETAINED_EARNINGS, label: 'Retained Earnings' },
  ],
  [AccountType.REVENUE]: [
    { value: AccountSubType.OPERATING_REVENUE, label: 'Operating Revenue' },
    { value: AccountSubType.OTHER_REVENUE, label: 'Other Revenue' },
  ],
  [AccountType.EXPENSE]: [
    { value: AccountSubType.OPERATING_EXPENSE, label: 'Operating Expense' },
    { value: AccountSubType.ADMINISTRATIVE_EXPENSE, label: 'Administrative Expense' },
    { value: AccountSubType.OTHER_EXPENSE, label: 'Other Expense' },
  ],
};

export default function AccountModal({ 
  isOpen, 
  onClose, 
  onSubmit, 
  account, 
  accounts, 
  loading = false 
}: AccountModalProps) {
  const [formData, setFormData] = useState<FormData>({
    code: '',
    name: '',
    description: '',
    type: AccountType.ASSET,
    subType: AccountSubType.CURRENT_ASSET,
    parentAccountId: '',
    metadata: {},
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!account;

  useEffect(() => {
    if (account) {
      setFormData({
        code: account.code,
        name: account.name,
        description: account.description || '',
        type: account.type,
        subType: account.subType,
        parentAccountId: account.parentAccountId || '',
        metadata: account.metadata || {},
      });
    } else {
      setFormData({
        code: '',
        name: '',
        description: '',
        type: AccountType.ASSET,
        subType: AccountSubType.CURRENT_ASSET,
        parentAccountId: '',
        metadata: {},
      });
    }
    setErrors({});
  }, [account, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Account code is required';
    } else if (accounts.some(acc => acc.code === formData.code && acc.id !== account?.id)) {
      newErrors.code = 'Account code already exists';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Account name is required';
    }

    if (!formData.type) {
      newErrors.type = 'Account type is required';
    }

    if (!formData.subType) {
      newErrors.subType = 'Account sub-type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name === 'type') {
      const newType = value as AccountType;
      const availableSubTypes = ACCOUNT_SUBTYPE_OPTIONS[newType];
      setFormData(prev => ({
        ...prev,
        type: newType,
        subType: availableSubTypes[0].value,
      }));
    } else if (name.startsWith('metadata.')) {
      const metadataKey = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          [metadataKey]: value,
        },
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        code: formData.code.trim(),
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        type: formData.type,
        subType: formData.subType,
        parentAccountId: formData.parentAccountId || undefined,
        metadata: Object.keys(formData.metadata).length > 0 ? formData.metadata : undefined,
      };

      await onSubmit(submitData);
      onClose();
    } catch (error: any) {
      console.error('Error submitting account:', error);
      toast.error(error.message || 'Failed to save account');
    } finally {
      setIsSubmitting(false);
    }
  };

  const availableSubTypes = ACCOUNT_SUBTYPE_OPTIONS[formData.type] || [];
  const potentialParentAccounts = accounts.filter(acc => 
    acc.type === formData.type && acc.id !== account?.id
  );

  const showBankingFields = formData.subType === AccountSubType.BANK;

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader
        title={isEditing ? 'Edit Account' : 'Create New Account'}
        subtitle={
          isEditing
            ? 'Update account information below.'
            : 'Fill in the account details below to create a new account.'
        }
        onClose={onClose}
      />
      <form onSubmit={handleSubmit}>
        <BrandModalBody>
                    <div className="space-y-6">
                      {/* Basic Information */}
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div>
                          <label htmlFor="code" className="block text-sm font-medium text-slate-700">
                            Account Code <span className="text-red-500">*</span>
                          </label>                          <input
                            type="text"
                            name="code"
                            id="code"
                            value={formData.code}
                            onChange={handleChange}
                            className={`mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm ${
                              errors.code ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                            }`}
                            placeholder="e.g., 1000"
                          />
                          {errors.code && <p className="mt-1 text-sm text-red-600">{errors.code}</p>}
                        </div>

                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-slate-700">
                            Account Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="name"
                            id="name"
                            value={formData.name}
                            onChange={handleChange}
                            className={`mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm ${
                              errors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                            }`}
                            placeholder="e.g., Cash on Hand"
                          />
                          {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                        </div>
                      </div>

                      <div>
                        <label htmlFor="description" className="block text-sm font-medium text-slate-700">
                          Description
                        </label>
                        <textarea
                          name="description"
                          id="description"
                          rows={3}
                          value={formData.description}
                          onChange={handleChange}
                          className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                          placeholder="Optional description of the account"
                        />
                      </div>

                      {/* Account Classification */}
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div>
                          <label htmlFor="type" className="block text-sm font-medium text-slate-700">
                            Account Type <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="type"
                            id="type"
                            value={formData.type}
                            onChange={handleChange}
                            className={`mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm ${
                              errors.type ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                            }`}
                          >
                            {ACCOUNT_TYPE_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {errors.type && <p className="mt-1 text-sm text-red-600">{errors.type}</p>}
                        </div>

                        <div>
                          <label htmlFor="subType" className="block text-sm font-medium text-slate-700">
                            Sub-Type <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="subType"
                            id="subType"
                            value={formData.subType}
                            onChange={handleChange}
                            className={`mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm ${
                              errors.subType ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                            }`}
                          >
                            {availableSubTypes.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {errors.subType && <p className="mt-1 text-sm text-red-600">{errors.subType}</p>}
                        </div>
                      </div>

                      {/* Parent Account */}
                      {potentialParentAccounts.length > 0 && (
                        <div>
                          <label htmlFor="parentAccountId" className="block text-sm font-medium text-slate-700">
                            Parent Account
                          </label>
                          <select
                            name="parentAccountId"
                            id="parentAccountId"
                            value={formData.parentAccountId}
                            onChange={handleChange}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                          >
                            <option key="empty-option-1753461297271" value="">No parent account</option>
                            {potentialParentAccounts.map(acc => (
                              <option key={acc.id} value={acc.id}>
                                {acc.code} - {acc.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Banking Information */}
                      {showBankingFields && (
                        <div className="bg-cyan-50 p-4 rounded-lg">
                          <h4 className="text-md font-medium text-blue-900 mb-3">Banking Information</h4>
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                              <label htmlFor="metadata.bankName" className="block text-sm font-medium text-slate-700">
                                Bank Name
                              </label>
                              <input
                                type="text"
                                name="metadata.bankName"
                                id="metadata.bankName"
                                value={formData.metadata.bankName || ''}
                                onChange={handleChange}
                                className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                                placeholder="e.g., First National Bank"
                              />
                            </div>

                            <div>
                              <label htmlFor="metadata.accountNumber" className="block text-sm font-medium text-slate-700">
                                Account Number
                              </label>
                              <input
                                type="text"
                                name="metadata.accountNumber"
                                id="metadata.accountNumber"
                                value={formData.metadata.accountNumber || ''}
                                onChange={handleChange}
                                className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                                placeholder="e.g., 12345678901"
                              />
                            </div>

                            <div>
                              <label htmlFor="metadata.branchCode" className="block text-sm font-medium text-slate-700">
                                Branch Code
                              </label>
                              <input
                                type="text"
                                name="metadata.branchCode"
                                id="metadata.branchCode"
                                value={formData.metadata.branchCode || ''}
                                onChange={handleChange}
                                className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                                placeholder="e.g., 250655"
                              />
                            </div>

                            <div>
                              <label htmlFor="metadata.currency" className="block text-sm font-medium text-slate-700">
                                Currency
                              </label>
                              <select
                                name="metadata.currency"
                                id="metadata.currency"
                                value={formData.metadata.currency || 'ZAR'}
                                onChange={handleChange}
                                className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                              >
                                <option key="ZAR-1753461297271" value="ZAR">South African Rand (ZAR)</option>
                                <option key="USD-1753461297271" value="USD">US Dollar (USD)</option>
                                <option key="EUR-1753461297271" value="EUR">Euro (EUR)</option>
                                <option key="GBP-1753461297271" value="GBP">British Pound (GBP)</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || loading}
            className="btn btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Saving...' : isEditing ? 'Update Account' : 'Create Account'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
} 